import React, { useState, useRef } from 'react';
import './index.css';
import Header from './components/Header';
import SearchBar from './components/SearchBar';
import AgentProgress from './components/AgentProgress';
import Report from './components/Report';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || 'https://nixus-backend.vercel.app' || 'http://localhost:3001';

const styles = {
  wrapper: { position: 'relative', zIndex: 1, maxWidth: '1200px', margin: '0 auto', padding: '0 40px 100px' },
  errorBox: {
    background: 'rgba(255,60,110,0.08)',
    border: '1px solid rgba(255,60,110,0.4)',
    borderRadius: '6px',
    padding: '18px 24px',
    fontFamily: 'var(--font-mono)',
    fontSize: '14px',
    color: 'var(--neon3)',
    marginBottom: '32px',
  },
  footer: {
    textAlign: 'center',
    paddingTop: '48px',
    borderTop: '1px solid var(--border)',
    fontFamily: 'var(--font-mono)',
    fontSize: '12px',
    color: 'var(--text3)',
    letterSpacing: '0.1em',
  },
};

export default function App() {
  const [phase, setPhase] = useState('idle');
  const [currentStep, setCurrentStep] = useState(1);
  const [stepLabels, setStepLabels] = useState({});
  const [company, setCompany] = useState('');
  const [reportData, setReportData] = useState(null);
  const [error, setError] = useState('');
  const reportRef = useRef(null);

  const runAgent = async (companyName) => {
    setCompany(companyName);
    setPhase('loading');
    setCurrentStep(1);
    setStepLabels({});
    setError('');
    setReportData(null);

    try {
      const res = await fetch(`${BACKEND_URL}/api/research`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ company: companyName }),
      });

      const reader = res.body.getReader();
      const decoder = new TextDecoder();
      let buffer = '';

      while (true) {
        const { done, value } = await reader.read();
        if (done) break;

        buffer += decoder.decode(value, { stream: true });
        const lines = buffer.split('\n');
        buffer = lines.pop();

        for (const line of lines) {
          if (!line.startsWith('data: ')) continue;
          const raw = line.slice(6).trim();
          if (raw === '[DONE]') break;

          const event = JSON.parse(raw);

          if (event.type === 'step') {
            setCurrentStep(event.step);
            setStepLabels(prev => ({ ...prev, [event.step]: { label: event.label, status: event.status } }));
          }

          if (event.type === 'report') {
            setReportData(event.data);
            setCurrentStep(6);
            setPhase('done');
            setTimeout(() => {
              reportRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
            }, 300);
          }

          if (event.type === 'error') throw new Error(event.message);
        }
      }
    } catch (err) {
      setError('AGENT ERROR: ' + err.message);
      setPhase('error');
    }
  };

  const reset = () => {
    setPhase('idle'); setReportData(null); setError(''); setCompany(''); setStepLabels({});
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  return (
    <>
      <div className="bg-grid" />
      <div className="bg-glow" />
      <div className="scanline" />
      <div style={styles.wrapper}>
        <Header />
        <SearchBar onSearch={runAgent} loading={phase === 'loading'} />
        <div style={{ height: '28px' }} />
        {(phase === 'loading' || phase === 'done') && (
          <AgentProgress currentStep={currentStep} company={company} stepLabels={stepLabels} />
        )}
        {phase === 'error' && <div style={styles.errorBox}>{error}</div>}
        {phase === 'done' && reportData && (
          <div ref={reportRef}><Report data={reportData} onReset={reset} /></div>
        )}
        <div style={{ height: '80px' }} />
        <div style={styles.footer}>
          NEXUS &nbsp;·&nbsp; <span style={{ color: 'var(--neon)' }}>AI INTELLIGENCE PLATFORM</span> &nbsp;·&nbsp; POWERED BY CLAUDE
        </div>
      </div>
    </>
  );
}
